import React, { useEffect, useMemo, useState } from 'react';
import { CommonChatMessages, type ChatMetaData } from '@circuitry-ai/doc-chatadvisor';
import {
    useAppDispatch,
    useFetchAdvisorAbilitiesMutation,
    useFetchConversationDetailsMutation,
    useGetAdvisorByIdQuery,
    updateActiveAdvisorAbilities,
} from '@circuitry-ai/doc-data';
import { CtySpinner, getUserInitials, integrateAdvisorAbilities } from '@circuitry-ai/doc-ui';
import { mapConversationLogMessages } from './adjustmentConversationMap';

type Props = {
    senderId: string;
    advisorId: string;
    messageId?: string;
};

export const AdjustmentConversationView: React.FC<Props> = ({ senderId, advisorId, messageId }) => {
    const dispatch = useAppDispatch();
    const [fetchConversationDetails] = useFetchConversationDetailsMutation();
    const [fetchAdvisorAbilities] = useFetchAdvisorAbilitiesMutation();
    const { data: advisorData } = useGetAdvisorByIdQuery(advisorId, { skip: !advisorId });
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [conversationMessages, setConversationMessages] = useState<any>(null);

    useEffect(() => {
        let cancelled = false;
        const load = async () => {
            setIsLoading(true);
            setError(null);
            try {
                const conversations = await fetchConversationDetails({
                    sender_id: senderId,
                }).unwrap();
                const advisorAbilitiesData = await fetchAdvisorAbilities(advisorId).unwrap();
                dispatch(updateActiveAdvisorAbilities(integrateAdvisorAbilities(advisorAbilitiesData)));
                if (!cancelled) setConversationMessages(conversations);
            } catch {
                if (!cancelled) setError('Failed to load conversation');
            } finally {
                if (!cancelled) setIsLoading(false);
            }
        };
        if (advisorId && senderId) {
            void load();
        }
        return () => {
            cancelled = true;
        };
    }, [advisorId, senderId, dispatch, fetchAdvisorAbilities, fetchConversationDetails]);

    const messages = useMemo(() => {
        const mapped = mapConversationLogMessages(conversationMessages?.conversation_log ?? []);
        if (!messageId) return mapped;
        const filtered = mapped.filter((msg: any) => {
            const id = msg?.message_id ?? msg?.metaData?.[6]?.message_id;
            return String(id) === String(messageId);
        });
        return filtered.length > 0 ? filtered : mapped;
    }, [conversationMessages, messageId]);

    if (isLoading) {
        return (
            <div className="flex justify-center py-8">
                <CtySpinner size="lg" />
            </div>
        );
    }

    if (error) {
        return <p className="text-default-500">{error}</p>;
    }

    const advisorLogo = advisorData?.data?.[0]?.assets?.[0]?.s3_path;
    const advisorUserName = advisorData?.data?.[0]?.name ?? 'Advisor';
    const userName = conversationMessages?.user_name?.split('.')[0] ?? 'You';
    const displayUser = ['None', 'NA'].includes(userName) ? 'You' : userName;

    return (
        <div className="flex flex-col gap-4">
            {messages.map((message: any, msgIndex: number) => {
                const isAdvisor = message?.role === 'bot';
                const chatMetaData: ChatMetaData = {
                    isAdvisor,
                    initials: displayUser !== 'You' ? getUserInitials(displayUser) : '',
                    advisorLogo,
                    username: isAdvisor ? advisorUserName : displayUser,
                    textMessage: typeof message?.message === 'string' ? message.message.trim() : '',
                    viewKnowledge: isAdvisor && message?.metaData?.[1]?.source_ids?.length > 0,
                    viewExpertAnswer: isAdvisor && message?.metaData?.[5]?.rephrased_query,
                    messageId: isAdvisor
                        ? `advisor-response-${message?.metaData?.[6]?.message_id ?? message?.message_id ?? msgIndex}`
                        : `advisor-query-${msgIndex}`,
                    figure_refs: message?.metaData?.[8]?.figure_refs ?? [],
                };
                return (
                    <CommonChatMessages
                        key={`${senderId}-${advisorId}-${message?.message_id ?? msgIndex}`}
                        expandView
                        msgIndex={msgIndex}
                        isAdvisorView={false}
                        chatMetaData={chatMetaData}
                        msg={message}
                        chatAdvisor={{ advisor_id: advisorId, sender_id: senderId }}
                        setIsSpeechMode={() => undefined}
                    />
                );
            })}
        </div>
    );
};
